import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import DateRangePicker from '@/components/dateRangePicker';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import Select from '@/components/Select';
import SkeletonLoader from '@/components/SkeletonLoader';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import {
  getCommonStatusLabel,
  getCommonStatusBadgeClass,
} from '@/constants/constant';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getClassDropdown,
  getStudentAnalytics,
  exportAnalyticsData,
} from '@/service/classService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, downloadFile, formatLastActive } from '@/utils';

interface IStudentReport {
  id: string;
  name: string;
  email: string;
  class_name: string;
  class_teacher_name: string;
  teacher_profile_pic?: string;
  lessons_count: number;
  level: string;
  status: string;
  last_active: number | null;
  avg_completion: number;
  profile_pic?: string;
}

interface IStudentsTabForm {
  search: string;
}

const StudentsTab = () => {
  const { formatText } = useFormatText();
  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { register, watch } = useForm<IStudentsTabForm>({
    defaultValues: {
      search: '',
    },
  });

  const searchQuery = watch('search');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [studentData, setStudentData] = useState<IStudentReport[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { settings } = useSettingsStore();

  const [selectedClass, setSelectedClass] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await getClassDropdown();
        if (response?.status && Array.isArray(response.data)) {
          const options = response.data.map(
            (c: { id: string; name: string }) => ({
              value: c.id,
              label: c.name,
            })
          );
          setClassOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch classes:', error);
      }
    };
    fetchClasses();
  }, []);

  const fetchStudents = async () => {
    if (isFirstLoad || (studentData.length === 0 && !debouncedSearchQuery)) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };
      if (debouncedSearchQuery) query.search = debouncedSearchQuery;
      if (selectedClass.length > 0) query.class_id = selectedClass;
      if (startDate)
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      if (endDate)
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);

      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      } else {
        query[`order[created_at]`] = 'DESC';
      }

      const response = await getStudentAnalytics(query);
      if (response?.status && Array.isArray(response.data)) {
        setStudentData(response.data);
        setTotalStudents(response?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch student analytics:', error);
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
      setIsFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearchQuery,
    selectedClass,
    startDate,
    endDate,
    pageSize,
    isFirstLoad,
    sortConfig,
  ]);

  // ─── Table Columns ───────────────────────────────────────────────────────────

  const studentColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'reports-analytics.table.student-name',
        width: '200px',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: IStudentReport) => (
          <div className="flex items-center gap-3">
            <UserAvatarList
              user={record.name}
              image={getAssetUrl(record?.profile_pic)}
            />
            <span className="text-sm font-medium text-neutral-primary">
              {record.name}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        title: 'reports-analytics.table.email',
        width: '220px',
        sorter: true,
        handleSort: () => handleSort('email'),
        render: (record: IStudentReport) => (
          <span className="text-sm text-neutral-secondary">{record.email}</span>
        ),
      },
      {
        key: 'class_name',
        title: 'reports-analytics.table.assigned-class',
        align: 'center' as const,
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('class_name'),
        render: (record: IStudentReport) => (
          <span className="text-sm font-medium text-neutral-primary">
            {record.class_name ?? '-'}
          </span>
        ),
      },
      {
        key: 'class_teacher_name',
        title: 'reports-analytics.table.teacher-name',
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('class_teacher_name'),
        render: (record: IStudentReport) =>
          record.class_teacher_name ? (
            <div className="flex items-center gap-2">
              <UserAvatarList
                user={record.class_teacher_name}
                image={getAssetUrl(record?.teacher_profile_pic)}
              />
              <span className="text-sm font-medium text-neutral-primary">
                {record.class_teacher_name}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        key: 'lessons_count',
        title: 'reports-analytics.table.lessons-created',
        align: 'center' as const,
        width: '150px',
        sorter: true,
        handleSort: () => handleSort('lessons_count'),
        render: (record: IStudentReport) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-pink-light-50 text-pink-secondary text-sm font-semibold">
            {record.lessons_count}
          </span>
        ),
      },
      {
        key: 'avg_completion',
        title: 'reports-analytics.table.avg-completion',
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('avg_completion'),
        render: (record: IStudentReport) => (
          <div className="flex flex-col gap-1">
            <ProgressBar value={record?.avg_completion ?? 0} />
          </div>
        ),
      },
      {
        key: 'status',
        title: 'reports-analytics.table.status',
        align: 'center' as const,
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: IStudentReport) => (
          <TagCapsule
            value={formatText(getCommonStatusLabel(record.status))}
            className={getCommonStatusBadgeClass(record.status)}
          />
        ),
      },
      {
        key: 'last_active',
        title: 'reports-analytics.table.last-update',
        width: '120px',
        sorter: true,
        align: 'center' as const,
        handleSort: () => handleSort('last_active'),
        render: (record: IStudentReport) => (
          <span className="text-sm text-neutral-secondary whitespace-nowrap">
            {record.last_active
              ? formatLastActive(record.last_active, formatText)
              : '-'}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings]
  );

  const handleExport = async (doc_type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        type: 2, // Student
      };
      if (debouncedSearchQuery) query.search = debouncedSearchQuery;
      if (selectedClass.length > 0) query.class_id = selectedClass;
      if (startDate)
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      if (endDate)
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);
      if (selectedRowKeys.length > 0) query.ids = selectedRowKeys;
      if (doc_type === 'csv') {
        query.doc_type = EExportType.CSV;
      } else if (doc_type === 'pdf') {
        query.doc_type = EExportType.PDF;
      }

      const response = await exportAnalyticsData(query);
      if (response) {
        const label = formatText('reports-analytics.tabs.students')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${doc_type}`);
      }
    } catch (error) {
      console.error('Failed to export students analytics:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const isFilterApplied =
    !!debouncedSearchQuery ||
    selectedClass.length > 0 ||
    !!startDate ||
    !!endDate;

  const hasNoStudents =
    !isFirstLoad &&
    !isScreenLoading &&
    studentData.length === 0 &&
    !isFilterApplied;

  const isStudentEmptySearch =
    !isFirstLoad &&
    !isScreenLoading &&
    studentData.length === 0 &&
    isFilterApplied;

  return (
    <div className="flex flex-col gap-6" data-testid="students-tab-container">
      {/* Filters Section */}
      {!hasNoStudents && (
        <div
          className="flex items-start justify-between gap-6 flex-wrap ps-1"
          data-testid="students-tab-filters"
        >
          <div className="flex flex-1 items-center gap-2.5 min-w-87.5 flex-wrap">
            {isScreenLoading ? (
              <SkeletonLoader className="max-w-70 h-11 w-full rounded-lg" />
            ) : (
              <Input
                name="search"
                placeholder={formatText('reports-analytics.student-searchbar')}
                leftIcon={{
                  icon: <IconSearch size={18} className="text-neutral-400" />,
                }}
                register={register}
                dataTestId="student-report-search-input"
                className="max-w-70"
                inputClassName="py-[13px]"
              />
            )}

            <div className="min-w-37.5">
              {isScreenLoading ? (
                <SkeletonLoader className="h-11 w-37.5 rounded-lg" />
              ) : (
                <Select
                  options={classOptions}
                  selected={selectedClass}
                  isClearable
                  onChange={(val) => setSelectedClass(val as string[])}
                  placeholder={formatText('reports-analytics.filters.class')}
                  isMulti={true}
                  isSearch={true}
                  dataTestId="class-report-filter-select"
                  className="w-37.5! py-3.25"
                />
              )}
            </div>
          </div>

          <div
            className="flex items-center gap-2.5"
            data-testid="students-tab-actions"
          >
            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-48 rounded-lg" />
            ) : (
              <DateRangePicker
                popupAlign="right"
                popUpClassName="right-0 rtl:left-0 rtl:right-auto"
                setFirstDate={setStartDate}
                setSecondDate={setEndDate}
                dataTestId="date-range-picker"
              />
            )}

            {isScreenLoading ? (
              <SkeletonLoader className="h-11 w-32 rounded-lg" />
            ) : (
              <ExportDropdown
                onCsvExport={() => handleExport('csv')}
                onPdfExport={() => handleExport('pdf')}
                disabled={studentData?.length === 0 || isExportLoading}
              />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl" data-testid="students-report-table">
        <CustomTable
          dataSource={studentData}
          columns={studentColumns}
          rowKey="id"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalStudents}
          pageSize={pageSize}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={setSelectedRowKeys}
          isEmptySearch={isStudentEmptySearch}
          isEmpty={hasNoStudents}
          showHeader={!hasNoStudents}
          hidePagination={hasNoStudents}
          emptyImgSrc="/not-found.svg"
          emptyTitle={formatText('reports-analytics.empty-title')}
          emptyDescription={formatText('reports-analytics.empty-description')}
          isLoading={isTableLoading || isFirstLoad}
          fieldName={sortConfig?.key}
          sortDirection={sortConfig?.direction}
          dataTestId="students-report-table-component"
        />
      </div>
    </div>
  );
};

export default StudentsTab;
